import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, filter, map, Observable, of, shareReplay, throwError } from 'rxjs';

import { CmgUiUtilities } from '../../../cmg-1003/components/common/cmg-ui-utils';
import { Cmg1003Model } from '../../../cmg-1003/models';
import { SecurityProfileQuery } from '../../../loan-state';
import { SecurityProfileModel } from '../../../loan-state/security-profile';
import * as Parties from '../../../parties/models/parties.model';
import { Models } from '../../models/global.models';
import { LayoutsRepository } from './layouts.repository';
import { LeadsRepository } from './leads.repository';
import { TagsRepository } from './tags.repository';
import { REQUIRES_SECURITY_PROFILE } from '../../../interceptors';
import { BusinessChannel } from '../../../models';
import { nonNullish } from '../../../shared';
import { uniqBy } from 'lodash';
import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';

/**
 * Route specific api stores
 * By default they are not injected in root since they are only needed by this route
 */
@Injectable({ providedIn: 'root' })
export class RouteApiService {
  leads = inject(LeadsRepository);
  layouts = inject(LayoutsRepository);
  tags = inject(TagsRepository);
  private readonly _securityProfileQuery = inject(SecurityProfileQuery);
  private readonly _environment = inject(APP_ENV);
  private readonly _http = inject(HttpClient);

  private _currentProfile: SecurityProfileModel = <any>{};
  private _baseUrl = this._environment.retail.apiUrl;

  columns$ = this._http.get<Models.LayoutColumn[]>(`${this._baseUrl}/layouts/columns`).pipe(shareReplay(1));

  /** Lead Type lookups */
  retailLeadTypes$ = this._http
    .get<Models.RetailLeadTypesLookup[]>(`${this._baseUrl}/lookups/leadtypes`, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
    })
    .pipe(
      map((r) => {
        // TODO: 16575 can remove this filter if removed from backend values
        // Removed Brokered Out from this filter in 23799.
        return r.filter((t) => t.name !== 'Builder - Brokered');
      }),
      // API response is mistyped. The ID is a string but the lead model requires a number
      map((r: Models.RetailLeadTypesLookup[]) => r.map((r2) => ({ name: r2.name, id: parseInt(r2.id) }) as any)),
      shareReplay(1),
    );

  /** All loan statuses from Lookups API. */
  loanStatusItems$ = this._http.get<Models.LoanStatusLookup[]>(`${this._baseUrl}/lookups/loanstatusitems`).pipe(shareReplay(1));

  partyCategories$ = this._http
    .get<Parties.Category[]>(`${this._baseUrl}/party/displayed`, { context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true) })
    .pipe(shareReplay(1));

  /** Is the currently logged in user profile a loan officer */
  // A securityProfileId of 1000048 indicates a loan officer
  isLoanOfficer$ = this._securityProfileQuery.selectActive().pipe(
    map((profile: SecurityProfileModel) => {
      return (
        (profile.businessChannel === BusinessChannel.Retail && profile.securityProfileId === 1000048) ||
        (profile.businessChannel === BusinessChannel.JV && profile.securityProfileId === 1000108)
      );
    }),
  );

  getRetailCompPlans$ = () => this._http.get<Models.RetailLoCompPlanTypesLookup[]>(`${this._baseUrl}/lookups/retailcompplans`);

  getReferralTypes$ = () => this._http.get<Models.ReferralTypeOption[]>(`${this._baseUrl}/lookups/loanreferrals`);

  // ==== START: Tags ==== //
  saveTagToLoan = (fileDataId: number, tagGuid: string) =>
    this._http.post(`${this._baseUrl}/all1003/file/${fileDataId}/tags/${tagGuid}`, {}, { headers: CmgUiUtilities.getSkipSpinnerHeaders() });

  deleteTagFromLoan = (fileDataId: number, tagGuid: string) =>
    this._http.delete(`${this._baseUrl}/all1003/file/${fileDataId}/tags/${tagGuid}`, {
      headers: CmgUiUtilities.getSkipSpinnerHeaders(),
    });

  saveTagToLead = (leadGuid: string, tagGuid: string) =>
    this._http.post(`${this._baseUrl}/leads/${leadGuid}/tags/${tagGuid}`, {}, { headers: CmgUiUtilities.getSkipSpinnerHeaders() });

  deleteTagFromLead = (leadGuid: string, tagGuid: string) =>
    this._http.delete(`${this._baseUrl}/leads/${leadGuid}/tags/${tagGuid}`, { headers: CmgUiUtilities.getSkipSpinnerHeaders() });

  getTagsForLoanApplication$ = (loanApplicationGuid: string) =>
    this._http.get<Models.Tag[]>(`${this._baseUrl}/loanapplications/${loanApplicationGuid}/tags`);

  saveTagToLoanApplication$ = (tagGuid: string, entityGuid: string) =>
    this._http.post(`${this._baseUrl}/loanapplications/${entityGuid}/tags/${tagGuid}`, {}, { headers: CmgUiUtilities.getSkipSpinnerHeaders() });

  deleteTagFromLoanApplication = (appGuid: string, tagGuid: string) =>
    this._http.delete(`${this._baseUrl}/loanapplications/${appGuid}/tags/${tagGuid}`, {
      headers: CmgUiUtilities.getSkipSpinnerHeaders(),
    });
  // ==== END: Tags ==== //

  /** Filtered Loans Pipeline */
  getFilteredLoans = (params: Models.PipelineSearchCriteria) =>
    this._http.post<Models.PipelineSearchResponse>(`${this._baseUrl}/pipeline`, params, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
    });

  /** Gets ALL loan applications for current user. */
  getLoanApplications = () =>
    this._http.get<Models.PipelineLoanApplication[]>(`${this._baseUrl}/loanapplications/pipeline`, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
    });

  /** Gets full loan application */
  getLoanApplicationById = (guid: string) => this._http.get<Models.LoanApplication>(`${this._baseUrl}/loanapplications/${guid}`);

  /** Gets servicing loans for current user. */
  getServicingLoans(originFilter: Models.PortfolioOriginFilter, marketing: Models.MarketingOriginFilter): Observable<Models.PipelineServicingLoan[]> {
    const isMarketable = marketing === Models.MarketingOriginFilter.OnlyMarketable || marketing === Models.MarketingOriginFilter.AllLoans;

    return this._http.get<Models.PipelineServicingLoan[]>(`${this._baseUrl}/servicingloans/pipeline`, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      params: {
        originFilter,
        isMarketable,
      },
    });
  }

  getCombinedServicingLoans(
    originFilter: Models.PortfolioOriginFilter,
    marketing: Models.MarketingOriginFilter,
  ): Observable<Models.PipelineServicingLoan[]> {
    const isMarketable = marketing === Models.MarketingOriginFilter.OnlyMarketable || marketing === Models.MarketingOriginFilter.AllLoans;

    return this._http.get<Models.PipelineServicingLoan[]>(`${this._baseUrl}/opportunityloans/pipeline`, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      params: {
        originFilter,
        isMarketable,
      },
    });
  }

  /** For "Opportunity Analysis" in slide-in panel. */
  getServicingLoanDetails = (servicerLoanNumber: string) =>
    this._http.get<Models.ServicingLoanDetails>(`${this._baseUrl}/servicingloans/${servicerLoanNumber}`, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
    });

  /** For "Opportunity Analysis" in slide-in panel. NEW API that will use Combined Loans Table */
  getServicingLoanDetailsNew = (
    loanId: string,
    sourceType: string,
    originFilter: Models.PortfolioOriginFilter,
    marketing: Models.MarketingOriginFilter,
  ) =>
    this._http.get<Models.ServicingLoanDetails>(`${this._baseUrl}/opportunityloans/${loanId}`, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      params: {
        sourceType,
        originFilter,
        marketing,
      },
    });

  getPartnersPipelineAccess() {
    return this._http
      .get<Models.PartnersAccessModel>(`${this._baseUrl}/partners/pipeline/access`, {
        context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      })
      .pipe(
        catchError(() => {
          return of({
            hasAccess: false,
            accessReason: 'Error loading permissions',
            partnerAccessType: '',
            profileId: '',
            securityProfileId: 0,
          });
        }),
      );
  }

  getLoLicenses$ = (userId: number, userOrgProfileId: number) => {
    const params: { userId: number; userOrgProfileId?: number } = { userId };
    if (nonNullish(userOrgProfileId)) {
      params.userOrgProfileId = userOrgProfileId;
    }
    return this._http.get<{ stateLicenses: Models.LoanOfficerLicense[] }>(`${this._baseUrl}/users/statelicenses`, {
      params,
    });
  };

  /** Gets All 1003 Loan Data */
  getAll1003Data = (fileDataId: number) =>
    this._http.get<Cmg1003Model>(`${this._baseUrl}/all1003/file/${fileDataId}`, { context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true) });

  getTagsForLoan$ = (fileDataId: number) => this._http.get<Models.Tag[]>(`${this._baseUrl}/all1003/file/${fileDataId}/tags`);

  getTagsForLead$ = (guid: string) => this._http.get<Models.Tag[]>(`${this._baseUrl}/leads/${guid}/tags`);

  /** Get a list of counties for a given state, IE "CA" */
  getCountiesForState$ = (state: string, skipSpinner = false) => {
    const url = `${this._baseUrl}/lookups/counties?stateAbbreviation=${state}`;
    return skipSpinner
      ? this._http.get<Models.Counties[]>(url, { headers: CmgUiUtilities.getSkipSpinnerHeaders() })
      : this._http.get<Models.Counties[]>(url);
  };

  /** Get a list of counties for a given state, IE "CA" */
  getZipCodeInfo$ = (zip: string, skipSpinner = false) => {
    const url = `${this._baseUrl}/lookups/zipcodes/${zip}`;
    return skipSpinner
      ? this._http.get<Models.ZipCodeRow>(url, { headers: CmgUiUtilities.getSkipSpinnerHeaders() })
      : this._http.get<Models.ZipCodeRow>(url);
  };

  // TODO: Validate model
  sendToLos$ = (guid: string) =>
    this._http.post<{ loanFileDataId: number; loanFileName: string }>(
      `${this._baseUrl}/leads/${guid}/converttoloan`,
      { guid },
      { context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true) },
    );

  sendApplicationToLos$ = (guid: string) =>
    this._http.post<{ loanFileDataId: string; loanFileName: string }>(`${this._baseUrl}/loanapplications/${guid}/submit`, { guid });

  validateDesktopUnderwriter = (fileDataId: number) => this._http.post(`${this._baseUrl}/aus/validatedesktopunderwriter/`, { fileDataId });

  validateLoanProspector = (fileDataId: number) => this._http.post(`${this._baseUrl}/aus/validateloanprospector/`, { fileDataId });

  /**
   * Returns list of Loan Officers (distinct by and sorted based on username).
   */
  getLoanOfficers$ = () =>
    this._http
      .get<Models.LoanOfficers[]>(`${this._baseUrl}/organization/loanofficers`, {
        context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          return this._handleError(err);
        }),
        map((loanOfficers) => {
          loanOfficers = loanOfficers ?? [];
          return uniqBy(loanOfficers, 'username')?.sort((a, b) => (a?.username || '')?.localeCompare(b?.username || ''));
        }),
      );
  getLoanOfficersByOrganization$ = (organizationId: number) =>
    this._http
      .get<
        Models.LoanOfficersByOrgId[]
      >(`${this._baseUrl}/organization/${organizationId}/loanofficers`, { context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true) })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          return this._handleError(err);
        }),
        map((loanOfficers) => {
          loanOfficers = loanOfficers ?? [];
          return uniqBy(loanOfficers, 'username')?.sort((a, b) => (a?.username || '')?.localeCompare(b?.username || ''));
        }),
      );

  loanOfficers$ = this.getLoanOfficers$().pipe(shareReplay(1));

  loanOfficersByOrganization$ = (organizationId: number) => this.getLoanOfficersByOrganization$(organizationId).pipe(shareReplay(1));

  getLoanOfficerLookups$ = this._http
    .get<Models.LoanOfficers[]>(`${this._baseUrl}/organization/loanofficers`, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
    })
    .pipe(
      catchError((err: HttpErrorResponse) => {
        return this._handleError(err);
      }),
      shareReplay(1),
    );

  getLoanLpPaList$ = (organizationId: number) =>
    this._http.get<Models.LoanLpPaList>(`${this._baseUrl}/organization/loanparties/${organizationId}`, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
    });

  getProcessorLookups$ = this._http
    .get<string[]>(`${this._baseUrl}/pipeline/processors`, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
    })
    .pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401 || err.status === 403) {
          return of([]);
        }
        return throwError(() => err);
      }),
      shareReplay(1),
    );

  getProductionAssistantLookups$ = this._http
    .get<string[]>(`${this._baseUrl}/pipeline/productionassistants`, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
    })
    .pipe(
      catchError((err: HttpErrorResponse) => {
        return this._handleError(err);
      }),
      shareReplay(1),
    );

  getPartnerNameLookups$ = this._http
    .get<string[]>(`${this._baseUrl}/pipeline/partnernames`, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
    })
    .pipe(
      catchError((err: HttpErrorResponse) => {
        return this._handleError(err);
      }),
      shareReplay(1),
    );

  copyLoan$ = (body: CopyLoanBody) =>
    this._http.post<{ fileDataId: number; fileName: string; reissueCreditResults: { reissueCreditStatus: string; message: string }[] }>(
      `${this._baseUrl}/loanfile/copy`,
      body,
      { headers: CmgUiUtilities.getSkipSpinnerHeaders(), context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true) },
    );

  emailBorrowers$ = (guid: string) => this._http.post(`${this._baseUrl}/loanapplications/${guid}/remind`, {});

  getLeadNotes = (guid: string) => this._http.get<Models.LeadNote>(`${this._baseUrl}/leads/${guid}/notes`);

  saveLeadNotes = (request: Partial<Models.LeadNote>) => this._http.put(`${this._baseUrl}/leads/${request.leadGUID}/notes`, request);

  getLoanApplicationNotes = (guid: string) =>
    this._http.get<Models.LoanApplicationNote>(`${this._environment.retail.apiUrl}/loanapplications/${guid}/notes`);

  saveLoanApplicationNotes = (request: Partial<Models.LoanApplicationNote>) =>
    this._http.post(`${this._environment.retail.apiUrl}/loanapplications/${request.loanApplicationGUID}/notes`, request);

  constructor() {
    this._securityProfileQuery
      .selectActive()
      .pipe(filter((t) => !!t))
      .subscribe(this._setCurrentProfile);
  }

  private readonly _setCurrentProfile = (currentProfile: any) => {
    this._currentProfile = currentProfile;
  };

  private _handleError(err: HttpErrorResponse) {
    if (err.status === 401 || err.status === 403) {
      return of([]);
    }
    return throwError(() => err);
  }
}

export type CopyLoanBody = {
  loanOfficerName: string;
  loanPurpose: 'Purchase' | 'Refinance';
  fileDataId: number;
  organizationCode: string;
  channel: Models.OriginationChannel;
};
